import { Disclosure, DisclosurePanel } from '@headlessui/react'
import { IconA2a, IconGraphQl, IconGrpc, IconMcpGray } from '@smartbear/swagger-portal-component-library'
import { useLocation } from 'react-router'
import { useMediaQuery } from 'usehooks-ts'

import { DefinitionMarkdownNavItem } from '../DefinitionMarkdownNavItem'
import { extractProductPathFromUrl } from '../helpers'
import { ApiNavItem } from '~/portal/components/ConsumerSideNavigation/ApiNavItem'
import { SideNavItem } from '~/portal/components/ConsumerSideNavigation/SideNavItem'
import appDefaults from '~/portal/portal.defaults.json'
import { ApiTocItem, PublishedTableOfContents } from '~/portal/types/content'
import { isPublishedA2aContent, isPublishedApiUrlContent, isPublishedDocumentContent, isPublishedGraphQlContent, isPublishedGrpcContent, isPublishedMcpContent } from '~/portal/utils/shared/content'

interface NestedSideNavItemProps {
  tocItem: PublishedTableOfContents;
  level: number;
  openIds: string[];
  onClick?: (e: any) => void;
  flat?: boolean;
  apiTitle?: string;
}
export const NestedSideNavItem = ({ tocItem, level, openIds, onClick, flat, apiTitle }: NestedSideNavItemProps) => {
  const location = useLocation()

  const productPath = extractProductPathFromUrl(location.pathname)
  const isActive = tocItem.path === productPath

  const isMobileView = useMediaQuery(appDefaults.media.mobile, { initializeWithValue: false })

  if (isPublishedApiUrlContent(tocItem)) {
    return <ApiNavItem
      key={`${tocItem.path}-${openIds.join('-')}`}
      item={tocItem}
      isActive={isActive}
      level={level}
      openIds={openIds}
      onClick={onClick}
      isMobile={isMobileView}
      flat={flat}
    />
  }
  
  if (isPublishedGraphQlContent(tocItem)) {
    const item = tocItem as any as ApiTocItem // TypeScript workaround 'path' does not exist on type 'never'

    return <DefinitionMarkdownNavItem
      key={`${item.path}-${openIds.join('-')}`}
      item={item}
      components={item.graphqlComponents}
      icon={<IconGraphQl />}
      isActive={isActive}
      level={level}
      openIds={openIds}
      onClick={onClick}
      isMobile={isMobileView}
      flat={flat}
      apiTitle={apiTitle}
    />
  }

  if (isPublishedMcpContent(tocItem)) {
    const item = tocItem as any as ApiTocItem // TypeScript workaround 'path' does not exist on type 'never'

    return <DefinitionMarkdownNavItem
      key={`${item.path}-${openIds.join('-')}`}
      item={item}
      components={item.mcpComponents}
      icon={<IconMcpGray />}
      isActive={isActive}
      level={level}
      openIds={openIds}
      onClick={onClick}
      isMobile={isMobileView}
      flat={flat}
      apiTitle={apiTitle}
    />
  }

  if (isPublishedA2aContent(tocItem)) {
    const item = tocItem as any as ApiTocItem // TypeScript workaround 'path' does not exist on type 'never'

    return <DefinitionMarkdownNavItem
      key={`${item.path}-${openIds.join('-')}`}
      item={item}
      components={item.a2aComponents}
      icon={<IconA2a />}
      isActive={isActive}
      level={level}
      openIds={openIds}
      onClick={onClick}
      isMobile={isMobileView}
      flat={flat}
      apiTitle={apiTitle}
    />
  }

  if (isPublishedGrpcContent(tocItem)) {
    const item = tocItem as any as ApiTocItem // TypeScript workaround 'path' does not exist on type 'never'

    return <DefinitionMarkdownNavItem
      key={`${item.path}-${openIds.join('-')}`}
      item={item}
      components={item.grpcComponents}
      icon={<IconGrpc />}
      isActive={isActive}
      level={level}
      openIds={openIds}
      onClick={onClick}
      isMobile={isMobileView}
      flat={flat}
      apiTitle={apiTitle}
    />
  }

  if (isPublishedDocumentContent(tocItem) && !tocItem.children?.length) {
    return (
      <SideNavItem
        path={`${tocItem.path}${location.search}`}
        isActive={isActive}
        title={tocItem.title}
        level={level}
        isExpandable={false}
        onClick={onClick}
        isMobile={isMobileView}
      />
    )
  }

  if (isPublishedDocumentContent(tocItem) && tocItem.children?.length) {
    return (
      <Disclosure as="div" defaultOpen={openIds.indexOf(tocItem.tableOfContentsId) >= 0} key={`${tocItem.path}-${openIds.join('-')}`}>
        {({ open }) => (
          <>
            <SideNavItem
              path={`${tocItem.path}${location.search}`}
              isActive={isActive}
              title={tocItem.title}
              isExpandable={true}
              level={level}
              open={open}
              onClick={onClick}
              isMobile={isMobileView}
            />
            <DisclosurePanel>
              {tocItem.children.map(tocItemChild => (
                <NestedSideNavItem key={tocItemChild.path} tocItem={tocItemChild} level={level + 1} openIds={openIds} onClick={onClick} />
              ))}
            </DisclosurePanel>
          </>
        )}
      </Disclosure >
    )
  }

  return null
}